import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {CostBenchmarkData} from '../../../../models/cost-benchmark-data.interface';
import {COST_PIE_OPERATIONS_COLORS, COST_PIE_OVERHEAD_COLORS} from '../../../../../constants/charts';
import {PointOptionsObject} from 'highcharts';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';

@Component({
  selector: 'app-operations-vs-overhead-pie-chart',
  templateUrl: './operations-vs-overhead-pie-chart.component.html',
  styleUrls: ['./operations-vs-overhead-pie-chart.component.scss']
})
export class OperationsVsOverheadPieChartComponent implements OnInit, OnChanges {

  textBelow!: string;
  chart!: Chart;
  chartData!: any[];
  totalOperationsText = '';
  totalOverheadText = '';
  averageOperationOverheadText = '';
  weightedAverageOperationOverheadText = '';
  countriesText = '';
  textBelowCode = 'kfcharts.WeeeSystem.Cost.OperationsVsOverhead.textBelow';
  isAdmin!: boolean;

  @Input()
  data!: CostBenchmarkData;
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;
  @Input()
  order!: string | undefined;

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(this.textBelowCode).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    this.setCountriesText();
    if (this.data) {
      // highcharts
      this.chartData = this.convertToChartData(this.data);
      this.setSpecifications();
      this.chart = new Chart({
        chart: {
          type: 'pie',
          options3d: {
            enabled: true,
            alpha: 45,
            beta: 0
          }
        },
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        plotOptions: {
          pie: {
            dataLabels: {
              enabled: true,
              format: '{point.name}'
            }
          }
        },
        caption: {
          text: ` <strong>‎</strong><br>
                  <strong>Total overheads: ${this.totalOverheadText}</strong><br>
                  <strong>Total operations: </strong>${this.totalOperationsText}<br>
                  <!--<strong>Average Operations vs Overheads: </strong>${this.averageOperationOverheadText}<br>
                  <strong>Weighted average Operations vs Overheads: </strong><br><br>-->
                  <strong>Countries: </strong> ${this.countriesText}
                `,
        },
        exporting: {
          tableCaption: this.title + ' ' + this.subtitle
        },
        accessibility: {
          point: {
            valueSuffix: '%'
          }
        },
        tooltip: {
          formatter(): string[] | undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              // return ['<div class="center"><b>' + this.point.name + '</b></div><br><br>',
              //   pointOptions.custom?.wfMax !== null ? `- Max value: ${formatNumber(pointOptions.custom?.wfMax)}  €/ton <br>` : `- Max value: <br>`,
              //   pointOptions.custom?.wfMin !== null ? `- Min value: ${formatNumber(pointOptions.custom?.wfMin)}  €/ton <br>` : `- Min value: <br>`,
              //   pointOptions.custom?.myWF !== null ? `- My value:  ${formatNumber(pointOptions.custom?.myWF)} €/ton <br>` : `- My value: <br>`,
              //   pointOptions.custom?.avgWF !== null ? `- Average value:  ${formatNumber(pointOptions.custom?.avgWF)} €/ton <br>` : `- Average value: <br>`,
              //   pointOptions.custom?.weightAvgWF ? `- Weighted Average:  ${formatNumber(pointOptions.custom?.weightAvgWF)} €/ton <br>` : `- Weighted Average: <br>`,
              // ];
              return ['<div class="center"><b>' + this.point.name + '</b></div><br><br>',
                pointOptions.custom?.avgWF !== null && Number(pointOptions.custom?.avgWF.toFixed(2)) !== 0 ? `- Average:  ${formatNumber(pointOptions.custom?.avgWF)} % <br>` : `- Average value: No costs <br>`,
                pointOptions.custom?.wfMin !== null &&  Number(pointOptions.custom?.wfMin.toFixed(2)) !== 0 ? `- Minimum: ${formatNumber(pointOptions.custom?.wfMin)}  % <br>` : `- Minimum: No costs <br>`,
                pointOptions.custom?.wfMax !== null &&  Number(pointOptions.custom?.wfMax.toFixed(2)) !== 0 ? `- Maximum: ${formatNumber(pointOptions.custom?.wfMax)}  % <br>` : `- Maximum: No costs <br>`,
              ];
            }
            // If not null, use the default formatter
            // return tooltip.defaultFormatter.call(this, tooltip);
            return undefined;
          }
        },
        series: [{
          name: 'Average',
          type: 'pie',
          data: this.chartData
        }]
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'], {state: {code: this.textBelowCode}}).then(
      () => {
        // do nothing
      }
    );
  }

  private convertToChartData(data: CostBenchmarkData): any[] {
    this.chart?.ref?.hideData();
    const chartData: any[] = [];
    let map = new Map<string, number>();
    map.set('Collection', data.wfaverageCollection);
    map.set('Logistics', data.wfaverageLogistics);
    map.set('Treatment', data.wfaverageTreatment);
    map.set('Clearing House', data.wfaverageClearingHouse);
    map.set('Communication', data.wfaverageCommunication);
    map.set('R&D', data.wfaverageRD);
    map.set('Office', data.wfaverageOffice);
    map.set('Other', data.wfaverageOther);

    switch (this.order) {
      case 'asc':
        map = new Map([...map.entries()].sort((a, b) => a[1] - b[1]));
        break;
      case 'desc':
        map = new Map([...map.entries()].sort((a, b) => b[1] - a[1]));
        break;
      case '':
        map = new Map([...map.entries()].sort());
        break;
    }

    if (data.countries?.length > 3) {
      map.forEach((value, key) => {
        if (this.displayUnits === '' || this.displayUnits === 'OPERATIONS') {
          switch (key) {
            case 'Collection':
              chartData.push({
                y: Number(data.wftotalCollection ? data.wftotalCollection.toFixed(2) : 0),
                name: 'Collection',
                color: COST_PIE_OPERATIONS_COLORS[0],
                custom: {
                  wfMax: data.wfmaxCollection,
                  wfMin: data.wfminCollection,
                  avgWF: data.wfaverageCollection
                }
              });
              break;
            case 'Logistics':
              chartData.push({
                y: Number(data.wftotalLogistics ? data.wftotalLogistics.toFixed(2) : 0),
                name: 'Logistics',
                color: COST_PIE_OPERATIONS_COLORS[1],
                custom: {
                  wfMax: data.wfmaxLogistics,
                  wfMin: data.wfminLogistics,
                  avgWF: data.wfaverageLogistics
                }
              });
              break;
            case 'Treatment':
              chartData.push({
                y: Number(data.wftotalTreatment ? data.wftotalTreatment.toFixed(2) : 0),
                name: 'Treatment',
                color: COST_PIE_OPERATIONS_COLORS[2],
                custom: {
                  wfMax: data.wfmaxTreatment,
                  wfMin: data.wfminTreatment,
                  avgWF: data.wfaverageTreatment
                }
              });
              break;
          }
        }
        if (this.displayUnits === '' || this.displayUnits === 'OVERHEAD') {
          switch (key) {
            case 'Clearing House':
              chartData.push({
                y: Number(data.wftotalClearingHouse ? data.wftotalClearingHouse.toFixed(2) : 0),
                name: 'Clearing House',
                color: COST_PIE_OVERHEAD_COLORS[0],
                custom: {
                  wfMax: data.wfmaxClearingHouse,
                  wfMin: data.wfminClearingHouse,
                  avgWF: data.wfaverageClearingHouse
                }
              });
              break;
            case 'Communication':
              chartData.push({
                y: Number(data.wftotalCommunication ? data.wftotalCommunication.toFixed(2) : 0),
                name: 'Communication',
                color: COST_PIE_OVERHEAD_COLORS[1],
                custom: {
                  wfMax: data.wfmaxCommunication,
                  wfMin: data.wfminCommunication,
                  avgWF: data.wfaverageCommunication
                }
              });
              break;
            case 'R&D':
              chartData.push({
                y: Number(data.wftotalRD ? data.wftotalRD.toFixed(2) : 0),
                name: 'R&D',
                color: COST_PIE_OVERHEAD_COLORS[2],
                custom: {
                  wfMax: data.wfmaxRD,
                  wfMin: data.wfminRD,
                  avgWF: data.wfaverageRD
                }
              });
              break;
            case 'Office':
              chartData.push({
                y: Number(data.wftotalOffice ? data.wftotalOffice.toFixed(2) : 0),
                name: 'Office',
                color: COST_PIE_OVERHEAD_COLORS[3],
                custom: {
                  wfMax: data.wfmaxOffice,
                  wfMin: data.wfminOffice,
                  avgWF: data.wfaverageOffice
                }
              });
              break;
            case 'Other':
              chartData.push({
                y: Number(data.wftotalOther ? data.wftotalOther.toFixed(2) : 0),
                name: 'Other',
                color: COST_PIE_OVERHEAD_COLORS[4],
                custom: {
                  wfMax: data.wfmaxOther,
                  wfMin: data.wfminOther,
                  avgWF: data.wfaverageOther
                }
              });
              break;
          }
        }
      });
    }
    return chartData;
  }

  private setCountriesText(): void {
    this.countriesText = '';
    if (this.data?.countries?.length > 3) {
      this.data.countries?.forEach((country, index) => {
        if (index === 0) {
          this.countriesText = ` ${country}`;
        } else {
          this.countriesText += `, ${country}`;
        }
      });
    }
  }

  private setSpecifications(): void {
    let maxOperation = 0;
    let maxOverhead = 0;
    let minOperation = 99999999999;
    let minOverhead = 99999999999;
    let totalOp = 0;
    let totalOv = 0;

    this.chartData.forEach(element => {
      if (['Collection', 'Logistics', 'Treatment'].includes(element.name)) {
        if (element.y > 0) {
          totalOp += element.y;
          if (maxOperation < element.y) {
            maxOperation = element.y;
          }
          if (minOperation > element.y) {
            minOperation = element.y;
          }
        }

      }


      if (['Clearing House', 'Communication', 'R&D', 'Office', 'Other'].includes(element.name)) {
        if (element.y > 0) {
          totalOv += element.y;
          if (maxOverhead < element.y) {
            maxOverhead = element.y;
          }
          if (minOverhead > element.y) {
            minOverhead = element.y;
          }
        }
      }

    });
    this.totalOperationsText = minOperation < 99999999999 ? ((minOperation/(totalOp + totalOv)*100).toFixed(2) + '% - ' + (maxOperation/(totalOp + totalOv)*100).toFixed(2) + '%') : ' - ';
    this.totalOverheadText = minOverhead < 99999999999 ? ((minOverhead/(totalOp + totalOv)*100).toFixed(2) + '% - ' + (maxOverhead/(totalOp + totalOv)*100).toFixed(2) + '%') : ' - ';
    /*
    switch(this.displayUnits) {
        case '':
          this.averageOperationOverheadText = ((totalOp + totalOv) / 8).toFixed(2) + '%'
          break;
        case 'OPERATIONS':
          this.averageOperationOverheadText = (totalOp / 3).toFixed(2) + '%'
          break;
        case 'OVERHEAD':
          this.averageOperationOverheadText = (totalOv / 5).toFixed(2) + '%'
          break;
    }
    this.weightedAverageOperationOverheadText = this.displayUnits === '' ? ((minOverhead).toFixed(2) + '% - ' + (maxOverhead).toFixed(2) + '%') : ' - ';
    */
  }

}
